import React, { useState } from 'react';
import { getUserRole } from '../../utils/auth';
import { saveComponent } from '../../api/libraryService';
import PropTypes from 'prop-types';
import IconButton from '../atoms/IconButton';
import Badge from '../atoms/Badge';
import Tag from '../atoms/Tag';
import Button from '../atoms/Button';
import Text from '../atoms/Text';
import ComponentPreview from '../molecules/ComponentPreview';
import { useToast } from '../atoms/Toast';

const ComponentCard = ({ component, onDelete, onEdit, onView, onSave, isPrivate, isSaved, canManage, extraActions }) => {
  const [saveStatus, setSaveStatus] = useState('idle'); // idle | saving | saved | error
  const toast = useToast();
  const { id, title, category, htmlCode, cssCode, jsCode, tags, updatedAt, visibility } = component;

  const handleDelete = async () => {
    const ok = await (toast?.confirm ? toast.confirm('Are you sure you want to delete this component?') : Promise.resolve(window.confirm('Are you sure you want to delete this component?')));
    if (ok) {
      try {
        await onDelete(id);
        toast?.success?.('Component deleted');
      } catch (error) {
        console.error('Error deleting component:', error);
        toast?.error?.('Failed to delete component');
      }
    }
  };

  const userRole = getUserRole();
  const isAdmin = userRole === 'ADMIN';
  const isSignedIn = !!userRole;
  const isPrivateComponent = isPrivate || visibility === 'private';
  const actions = (isAdmin || canManage) ? (
    <>
      <IconButton
        icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
          </svg>
        }
        onClick={() => onEdit?.(id)}
        variant="primary"
      />
      <IconButton
        icon={
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        }
        onClick={handleDelete}
        variant="danger"
      />
    </>
  ) : null;

  // Save button for non-admin users (hidden for private or already-saved items)
  const handleSave = async () => {
    console.log('Saving component with id:', id);
    setSaveStatus('saving');
    try {
      await saveComponent(id);
      setSaveStatus('saved');
      toast?.success?.('Saved to your library');
      if (onSave) onSave(id);
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (error) {
      setSaveStatus('error');
      console.log('Error saving component:', error);
      toast?.error?.('Failed to save component');
      setTimeout(() => setSaveStatus('idle'), 2000);
    }
  };

  return (
    <div className="bg-white rounded-lg overflow-hidden group hover:shadow-lg transition-all duration-200">
      {/* Preview Area */}
      <div className="aspect-w-16 aspect-h-9 bg-gray-50 relative">
        <div className="w-full h-full">
          <ComponentPreview
            htmlCode={htmlCode}
            cssCode={cssCode}
            jsCode={jsCode}
          />
        </div>

        {/* Category Badge */}
        <div className="absolute top-3 left-3">
          <Badge variant="purple" className="capitalize">
            {category || 'UI Element'}
          </Badge>
        </div>

        {/* Quick Actions */}
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-2">
          {actions}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-4">
        <Text variant="h4" className="mb-3">{title}</Text>
        
        {/* Tags */}
        {tags && tags.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {tags.map((tag, index) => (
              <Tag key={index}>{tag}</Tag>
            ))}
          </div>
        )}
      </div>

  {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 flex justify-between items-center border-t border-gray-100">
        <Text variant="small">
          Updated {new Date(updatedAt || Date.now()).toLocaleDateString()}
        </Text>
        <div className="flex gap-2 items-center">
          <Button
            variant="default"
            size="sm"
            rightIcon={
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            }
            onClick={() => onView?.(id)}
            className="text-purple-600 hover:text-purple-700"
          >
            View Details
          </Button>
          {isSaved && onDelete && (
            <Button
              variant="danger"
              size="sm"
              onClick={handleDelete}
              className="ml-2"
            >
              Remove
            </Button>
          )}
          {!isAdmin && isSignedIn && !isPrivateComponent && !isSaved && (
            <Button
              variant="primary"
              size="sm"
              onClick={handleSave}
              disabled={saveStatus === 'saving' || saveStatus === 'saved'}
              className="ml-2"
            >
              {saveStatus === 'idle' && 'Save'}
              {saveStatus === 'saving' && 'Saving...'}
              {saveStatus === 'saved' && 'Saved!'}
              {saveStatus === 'error' && 'Error'}
            </Button>
          )}
          {extraActions}
        </div>
      </div>
    </div>
  );
};

ComponentCard.propTypes = {
  component: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    category: PropTypes.string,
    htmlCode: PropTypes.string,
    cssCode: PropTypes.string,
    jsCode: PropTypes.string,
    tags: PropTypes.arrayOf(PropTypes.string),
    updatedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }).isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onView: PropTypes.func,
  onSave: PropTypes.func,
  isPrivate: PropTypes.bool,
  isSaved: PropTypes.bool,
  canManage: PropTypes.bool,
  extraActions: PropTypes.node
};

export default ComponentCard;
